import React, { FC, HTMLAttributes, useCallback, useState } from 'react';
import BasicModal from '../BasicModal/BasicModal';
import { Button, Heading } from 'components/ui';
import classNames from 'classnames';
import { RootState } from 'store/rootReducer';
import { useDispatch, useSelector } from 'react-redux';
import { Field, Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { toggleAuthoriseAccounts } from 'modules/layout/store/actions';
import { TextInputWrapper } from 'components/forms';
import { Notification, Error, SuccessMessage } from 'components';
import { useDispatchRequest } from '@redux-requests/react';
import { listPrivateApp } from 'modules/userApps/store/actions';
import { useParams } from 'react-router-dom';
import styles from './AuthoriseAccountsModal.module.scss';
import { validate as uuidValidate } from 'uuid';
import { t, dangerousHTML } from 'utils';

export interface IAuthoriseAccountsModal extends HTMLAttributes<HTMLDivElement> {}

const AuthoriseAccountsModal: FC<IAuthoriseAccountsModal> = (props) => {
  const { className } = props;
  const [authorisedAccount, setAuthorisedAccount] = useState('');
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const dispatchRequest = useDispatchRequest();

  const open = useSelector((state: RootState) => state.layout.authoriseAccounts);
  const rootClassName = classNames(styles.root, className);

  const handleCloseModal = useCallback(() => {
    dispatch(toggleAuthoriseAccounts(false));
  }, [dispatch]);

  const handleAuthoriseAccountsSubmit = useCallback(
    async (value) => {
      const { uuid } = value || {};
      if (!uuid) return;

      try {
        const { error, data } = await dispatchRequest(listPrivateApp(id, uuid));
        if (error) {
          const code: string = error?.data?.errors?.[0]?.extensions?.code;
          return {
            [FORM_ERROR]:
              code === 'AlreadyAuthorized'
                ? 'errors.authorise.accounts.companyNotFound'
                : 'errors.authorise.accounts',
          };
        }
        if (data.listPrivateApp?.name) {
          // Add name to success message
          setAuthorisedAccount(data.listPrivateApp.name);
        }
      } catch (error) {
        return { [FORM_ERROR]: 'errors.authorise.accounts' };
      }
    },
    [dispatchRequest, id]
  );

  return (
    <BasicModal className={rootClassName} open={open} onClose={handleCloseModal}>
      <Form
        onSubmit={handleAuthoriseAccountsSubmit}
        render={({
          pristine,
          submitError,
          submitFailed,
          handleSubmit,
          submitSucceeded,
          hasValidationErrors,
          dirtySinceLastSubmit,
          modifiedSinceLastSubmit,
          ...props
        }) => (
          <form onSubmit={handleSubmit}>
            <Heading className='mb-3' icon='shield-alt'>
              {t('authorize.accounts.modal.title')}
            </Heading>
            <Field
              name='uuid'
              component={TextInputWrapper}
              label='form.step.app.authorization.privateKey.label'
              placeholder='form.step.app.authorization.privateKey.placeholder'
              validate={(value) =>
                value ? (uuidValidate(value) ? undefined : t('error.guid.is.wrong')) : undefined
              }
              className='mb-2'
            />
            <Notification
              className={
                submitError || (!modifiedSinceLastSubmit && submitSucceeded) ? 'mb-2' : 'mb-4'
              }
              title='dont.forget.to.install.the.app'
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: dangerousHTML(t('authorise.accounts.message.text')),
                }}
              />
            </Notification>
            {!modifiedSinceLastSubmit && submitError && (
              <Error className='mb-2' isError>
                {t(submitError)}
              </Error>
            )}
            {!modifiedSinceLastSubmit && submitSucceeded && authorisedAccount && (
              <SuccessMessage className='mb-2' isSuccess>
                {t('authorise.accounts.successMessage.text', [
                  { key: 'ACCOUNT', value: authorisedAccount },
                ])}
              </SuccessMessage>
            )}
            <Button
              className='mr-2'
              onClick={handleCloseModal}
              type='button'
              variant='secondary'
              inline
              bold
            >
              {t('cancel')}
            </Button>
            <Button
              disabled={
                (submitFailed && !dirtySinceLastSubmit) ||
                hasValidationErrors ||
                pristine ||
                (submitSucceeded && !dirtySinceLastSubmit)
              }
              inline
            >
              {t('authorise')}
            </Button>
          </form>
        )}
      />
    </BasicModal>
  );
};

export default AuthoriseAccountsModal;
